/**
 * FeedFilters — status / category / restaurant filters for the feed.
 * Chips use the control radius; selected chip = ink-bordered (action), not green.
 */
import type { ReactNode } from "react";
import type { AnomalyCategory, AnomalyStatus } from "@/types/contract";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "@/lib/anomaly";
import { cn } from "@/lib/utils";

export interface FeedFilterState {
  status: AnomalyStatus | "all";
  category: AnomalyCategory | "all";
  restaurantId: string;
}

interface Props {
  value: FeedFilterState;
  restaurants: { id: string; name: string }[];
  onChange: (next: FeedFilterState) => void;
}

const STATUS_OPTIONS: { key: AnomalyStatus | "all"; label: string }[] = [
  { key: "all", label: "Все" },
  { key: "open", label: "Открытые" },
  { key: "acknowledged", label: "В работе" },
  { key: "resolved", label: "Решённые" },
];

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "focus-ring rounded-control px-3 py-1.5 text-sm font-semibold transition-colors",
        active
          ? "border border-ink bg-surface text-ink"
          : "border border-border bg-surface-2 text-muted hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

export function FeedFilters({ value, restaurants, onChange }: Props) {
  return (
    <div className="mb-4 flex flex-col gap-3">
      {restaurants.length > 1 && (
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted">Точка</span>
          <select
            value={value.restaurantId}
            onChange={(e) =>
              onChange({ ...value, restaurantId: e.target.value })
            }
            className="focus-ring rounded-control border border-border bg-surface px-3 py-1.5 text-sm font-semibold text-ink"
          >
            {restaurants.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="no-scrollbar flex gap-2 overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <Chip
            key={s.key}
            active={value.status === s.key}
            onClick={() => onChange({ ...value, status: s.key })}
          >
            {s.label}
          </Chip>
        ))}
      </div>

      <div className="no-scrollbar flex gap-2 overflow-x-auto">
        <Chip
          active={value.category === "all"}
          onClick={() => onChange({ ...value, category: "all" })}
        >
          Все категории
        </Chip>
        {CATEGORY_ORDER.map((c) => (
          <Chip
            key={c}
            active={value.category === c}
            onClick={() => onChange({ ...value, category: c })}
          >
            {CATEGORY_LABELS[c]}
          </Chip>
        ))}
      </div>
    </div>
  );
}
